"use client";

import { buildTree, ITreeViewItemModel } from "@/common/AppTreeView";
import { DotSquare, MinusSquare, PlusSquare } from "@/icons";
import theme from "@/material";
import { AppOptionType, IStyleProps } from "@/models";
import {
  Box,
  FormControl,
  InputLabelProps,
  MenuItem,
  Select,
  SelectProps,
} from "@mui/material";
import { RichTreeView, treeItemClasses } from "@mui/x-tree-view";
import { useTreeViewApiRef } from "@mui/x-tree-view/hooks";
import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Control,
  Controller,
  ControllerProps,
  FieldPath,
  FieldValues,
  RegisterOptions,
} from "react-hook-form";
import AppFromLabelField from "./AppFromLabelField";
import { appSelectStyles } from "../AppSelect";

const AppFromControlSelectTree = <T extends FieldValues>({
  label,
  control,
  name,
  rules,
  controlProps,
  labelProps,
  selectProps,
  classNameContainer,
  helper,
  treeData,
  defaultValue,
}: AppFromControlSelectTreeProps<T>) => {
  const apiRef = useTreeViewApiRef();
  const [isSelectOpen, setIsSelectOpen] = useState<boolean>(false);
  const [selectedItem, setSelectedItem] = useState<{
    id: string;
    label: string;
  } | null>(null);

  const allIds = useMemo(
    () =>
      treeData.flatMap((item) => [
        item.id,
        ...(item.children?.map((child) => child.id) || []),
      ]),
    [treeData]
  );

  const items = useMemo(() => buildTree(treeData), [treeData]);

  const handleClickItemTree = useCallback(
    (
      event: React.MouseEvent,
      itemId: string,
      onChange: (value: string) => void
    ) => {
      const item = apiRef?.current?.getItem(itemId);
      if (item) {
        setSelectedItem({ id: item.id, label: item.label });
        onChange(item.id);
        setIsSelectOpen(false);
      }
    },
    [apiRef]
  );

  useEffect(() => {
    if (defaultValue?.id) {
      setSelectedItem({
        id: defaultValue.id,
        label: defaultValue.label as string,
      });
    }
  }, [defaultValue]);

  return (
    <AppFromLabelField
      label={label}
      className={classNameContainer}
      required={labelProps?.required}
      helper={helper}
      labelProps={labelProps}
    >
      <Controller
        name={name}
        control={control}
        rules={rules}
        render={({ field: { onChange, value, ...otherFieldProps } }) => (
          <FormControl fullWidth>
            <Select
              labelId="tree-select-label"
              label=""
              sx={appSelectStyles}
              value={value || ""}
              displayEmpty
              open={isSelectOpen}
              onClose={() => setIsSelectOpen(false)}
              onOpen={() => setIsSelectOpen(true)}
              onChange={(event) => {
                // Khi chọn onChange sẽ clear giá trị về "Chọn làm thư mục gốc"
                setSelectedItem(null);
                onChange("");
              }}
              renderValue={() =>
                selectedItem ? selectedItem.label : "Thư mục gốc"
              }
              {...otherFieldProps}
              {...selectProps}
            >
              <MenuItem sx={styleObj.defaultSelect} value="">
                <Box sx={styleObj.icon}>
                  <DotSquare />
                </Box>
                Chọn làm thư mục gốc
              </MenuItem>
              <Box>
                <RichTreeView
                  selectedItems={defaultValue?.id || value}
                  apiRef={apiRef}
                  items={items}
                  defaultExpandedItems={allIds}
                  slots={{
                    expandIcon: PlusSquare,
                    collapseIcon: MinusSquare,
                    endIcon: DotSquare,
                  }}
                  onItemClick={(event, itemId) =>
                    handleClickItemTree(event, itemId, onChange)
                  }
                  sx={styleObj.tree}
                />
              </Box>
            </Select>
          </FormControl>
        )}
        {...controlProps}
      />
    </AppFromLabelField>
  );
};

type AppFromControlSelectTreeProps<T extends FieldValues> = {
  label?: string | ReactNode;
  control: Control<any>;
  name: FieldPath<T>;
  options?: AppOptionType[];
  isHasAllOption?: false;
  rules?: RegisterOptions;
  controlProps?: Omit<ControllerProps, "render" | "name" | "control">;
  labelProps?: InputLabelProps;
  selectProps?: SelectProps;
  classNameContainer?: string;
  helper?: string | ReactNode;
  treeData: ITreeViewItemModel[];
  defaultValue?: { id?: string; label?: string };
};

export default AppFromControlSelectTree;

const styleObj: IStyleProps = {
  tree: {
    border: "1px solid rgba(0, 0, 0, 0.12)",
    "& > li:not(:first-child)": {
      borderTop: "1px solid rgba(0, 0, 0, 0.12)",
    },
    ".MuiTreeItem-root": {
      color: "common.black",
      position: "relative",
      [`& .${treeItemClasses.groupTransition}`]: {
        marginLeft: theme.spacing(3.5),
      },
      ".MuiCollapse-root": {
        paddingLeft: "32px",
      },
    },

    "& .MuiTreeItem-iconContainer": {
      fontSize: "14px",
      color: "primary.main",
      display: "flex",
      alignItems: "center",
      "& svg": {
        fontSize: "inherit",
      },
      "& .icon-DotSquare": {
        opacity: 1,
      },
    },
  },
  defaultSelect: {
    px: 1,
    fontSize: 14,
  },
  icon: {
    fontSize: 18,
    color: "primary.main",
    mr: 0.6,
    // opacity: 0.3,
  },
};
